// src/core.ts

import { findBestMatchLocale, interpolate } from './utils';
import type {
  I18nConfig,
  I18nInstance,
  Locale,
  TranslationKeyValues,
} from './types';

const DEFAULT_STORAGE_KEY = 'mm-locale';

export function createI18n(config: I18nConfig): I18nInstance {
  // Validate config basics
  if (!config.defaultLocale) {
    throw new Error('[i18n] `defaultLocale` is required.');
  }
  if (!config.resources || Object.keys(config.resources).length === 0) {
    throw new Error('[i18n] `resources` must be provided and non-empty.');
  }
  if (!config.resources[config.defaultLocale]) {
    console.warn(
      `[i18n] Default locale "${config.defaultLocale}" not found in resources. Translation fallbacks may not work as expected.`
    );
  }

  const resolvedConfig: Required<I18nConfig> = {
    storageKey: DEFAULT_STORAGE_KEY,
    ...config,
  };

  const availableLocales = Object.keys(resolvedConfig.resources);

  const translate = (
    key: string,
    fallbackValueOptions?:
      | string // t(key, fallback)
      | TranslationKeyValues // t(key, values)
      | { args?: TranslationKeyValues; fallback?: string }, // t(key, {args: values, fallback })
    explicitLocale?: Locale
  ): string => {
    let values: TranslationKeyValues | undefined;
    let fallback: string | undefined;

    // Pattern matching for different argument combinations
    if (typeof fallbackValueOptions === 'string') {
      fallback = fallbackValueOptions;
    } else if (fallbackValueOptions) {
      if (
        'fallback' in fallbackValueOptions ||
        'args' in fallbackValueOptions
      ) {
        const opts = fallbackValueOptions as {
          fallback?: string;
          args?: TranslationKeyValues;
        };

        values = opts.args;
        fallback = opts.fallback;
      } else {
        values = fallbackValueOptions as TranslationKeyValues;
      }
    }

    const targetLocale = explicitLocale || resolvedConfig.defaultLocale;
    let translation = resolvedConfig.resources[targetLocale]?.[key];

    // Fallback to default locale if requested locale or key is missing
    if (!translation && targetLocale !== resolvedConfig.defaultLocale) {
      const defaultTranslation =
        resolvedConfig.resources[resolvedConfig.defaultLocale]?.[key];
      if (typeof defaultTranslation === 'string') {
        if (explicitLocale) {
          console.warn(
            `[i18n] Translation key "${key}" not found for locale "${explicitLocale}". Falling back to default locale "${resolvedConfig.defaultLocale}".`
          );
        }
        translation = defaultTranslation;
      }
    }

    // If still not found, use provided fallback or return the key itself
    if (!translation) {
      if (fallback !== undefined) {
        console.warn(
          `[i18n] Translation key "${key}" not found for locale "${targetLocale}" or default locale "${resolvedConfig.defaultLocale}". Using provided fallback.`
        );
        // Use the provided fallback and interpolate if values were passed
        return interpolate(fallback, values);
      } else {
        console.warn(
          `[i18n] Translation key "${key}" not found for locale "${targetLocale}" or default locale "${resolvedConfig.defaultLocale}". Returning key.`
        );
        // Still try to interpolate the key itself if values were passed
        return interpolate(key, values);
      }
    }

    // Perform interpolation
    return interpolate(translation, values);
  };

  const getLocaleFromHeaders = (
    headers: Headers | Record<string, string>
  ): Locale => {
    let acceptLanguageHeader: string | null = null;
    if (typeof headers?.get === 'function') {
      acceptLanguageHeader = headers.get('accept-language');
    } else if (headers) {
      // Assumes plain object
      acceptLanguageHeader =
        (headers as Record<string, string>)['accept-language'] ||
        (headers as Record<string, string>)['Accept-Language'] ||
        null;
    }

    return findBestMatchLocale(
      acceptLanguageHeader,
      availableLocales,
      resolvedConfig.defaultLocale
    );
  };

  const getAvailableLocales = (): Locale[] => {
    return [...availableLocales]; // Return a copy
  };

  const instance: I18nInstance = {
    config: resolvedConfig,
    t: translate as I18nInstance['t'],
    getLocaleFromHeaders: getLocaleFromHeaders,
    getAvailableLocales: getAvailableLocales,
  };

  return instance;
}
